import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { handleError } from '../../utils/errorHandler';
import EmailDetailsModal from '../../components/modal/EmailDetailsModal';
import EmailContentModal from '../../components/modal/EmailContentModal';
import { emailLogsApi } from '../../repositories/emailLogs';
import {
  EnvelopeIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';
import { fromBase64 } from '../../utils/base64';
import PageHeader from '../../components/ui/PageHeader';
import { HOME_CRUMB } from '../../components/ui/Breadcrumbs';
import DataTable, {
  DataTableColumn,
  dataTableRowClass,
  dataTableTdClass,
} from '../../components/ui/DataTable';
import EmailStatusBadge from '../../components/ui/EmailStatusBadge';
import { Skeleton, SkeletonTableRows } from '../../components/ui/skeleton';
import { cn } from '../../DashboardPage/lib/utils';
import Button from '../../components/ui/Button';

// Email Log interface based on API response
interface EmailLog {
  id: string;
  userId: number;
  templateId: string;
  pitchId: number;
  emailContentId: string;
  contactId: string;
  trackId: string;
  subject: string;
  status: 'SENT' | 'FAILED' | 'PENDING' | 'QUEUED';
  reason: string | null;
  scheduledFor: string;
  sendAt: string;
  createdAt: string;
  updatedAt: string;
  contact: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    company: string;
  };
  pitch: {
    id: number;
    releaseTitle: string;
    artistName: string;
  };
  template?: {
    id: string;
    name: string;
  };
}

interface EmailLogDetail {
  logId: string;
  status: string;
  reason: string | null;
  /** Present only for emails that were scheduled rather than sent immediately. */
  scheduledFor?: string | null;
  sendAt: string;
  body: string;
  subject: string;
  templateName?: string;
  contact: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    company: string;
  };
}

interface EmailContent {
  subject: string;
  body: string;
}

const EmailLogs: React.FC = () => {
  const { pitchId } = useParams<{ pitchId: string }>();

  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Pitch info from first log
  const [pitchInfo, setPitchInfo] = useState<{ releaseTitle: string; artistName: string } | null>(null);

  // Modal state for email details
  const [selectedEmailDetail, setSelectedEmailDetail] = useState<EmailLogDetail | null>(null);
  const [isResending, setIsResending] = useState(false);

  // Email content modal state
  const [emailContent, setEmailContent] = useState<EmailContent | null>(null);

  // Refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (pitchId) {
      fetchEmailLogs(Number(pitchId));
    }
  }, [pitchId]);


  const fetchEmailLogs = async (id: number) => {
    try {
      setLoading(true);
      // Fetch all logs (max 30 per pitch, no pagination needed)
      const response = await emailLogsApi.getEmailLogsByPitch(id, { limit: 30 });

      if (response?.data?.success) {
        const logs = response.data.emailLogs || [];
        setEmailLogs(logs);

        // Set pitch info from first log
        if (logs.length > 0 && logs[0].pitch) {
          setPitchInfo({
            releaseTitle: logs[0].pitch.releaseTitle,
            artistName: logs[0].pitch.artistName,
          });
        }
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    if (!pitchId) return;
    setIsRefreshing(true);
    try {
      const response = await emailLogsApi.getEmailLogsByPitch(Number(pitchId), { limit: 30 });
      if (response?.data?.success) {
        const logs = response.data.emailLogs || [];
        setEmailLogs(logs);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle check bounced
  // const handleCheckBounced = async () => {
  //   if (!pitchId) return;
  //   setIsRefreshing(true);
  //   try {
  //     const response = await emailLogsApi.checkBounceStatus(Number(pitchId));
  //     if (response?.data?.success) {
  //       const data = response.data.data;
  //       const logs = data?.emailLogs || [];
  //       setEmailLogs(logs);

  //       // Use bouncedCount from API response
  //       const bouncedCount = data?.bouncedCount || 0;
  //       const totalChecked = data?.totalChecked || logs.length;

  //       if (bouncedCount > 0) {
  //         Swal.fire({
  //           title: 'Bounced Emails Detected',
  //           html: `<p><strong>${bouncedCount}</strong> out of <strong>${totalChecked}</strong> email${totalChecked > 1 ? 's' : ''} bounced or failed.</p>`,
  //           icon: 'warning',
  //           timer: 4000,
  //           timerProgressBar: true,
  //           showConfirmButton: true,
  //           confirmButtonText: 'OK',
  //         });
  //       } else {
  //         Swal.fire({
  //           title: 'No Bounced Emails',
  //           text: `All ${totalChecked} email${totalChecked > 1 ? 's were' : ' was'} delivered successfully.`,
  //           icon: 'success',
  //           timer: 3000,
  //           timerProgressBar: true,
  //           showConfirmButton: true,
  //           confirmButtonText: 'OK',
  //         });
  //       }
  //     }
  //   } catch (error) {
  //     handleError(error);
  //   } finally {
  //     setIsRefreshing(false);
  //   }
  // };

  // Handle resend email
  const handleResendEmail = async () => {
    if (!selectedEmailDetail) return;

    try {
      setIsResending(true);
      const response = await emailLogsApi.resendEmail(selectedEmailDetail.logId);

      if (response?.data?.success) {
        Swal.fire({
          title: 'Success!',
          text: 'Email has been queued for resending.',
          icon: 'success',
          confirmButtonText: 'OK',
        });
        setSelectedEmailDetail(null);
        // Refresh the logs
        if (pitchId) {
          fetchEmailLogs(Number(pitchId));
        }
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsResending(false);
    }
  };

  // Decode base64 and replace contact placeholders
  const decodeAndReplaceContent = (data: EmailLogDetail): EmailContent => {
    // Decode base64 content using safe UTF-8 decoder
    let htmlContent = fromBase64(data?.body);

    // Replace contact placeholders with actual values
    htmlContent = htmlContent
      .replace(/\{\{firstName\}\}/g, data.contact.firstName)
      .replace(/\{\{lastName\}\}/g, data.contact.lastName)
      .replace(/\{\{company\}\}/g, data.contact.company)
      .replace(/\{\{email\}\}/g, data.contact.email);

    return {
      subject: data.subject,
      body: htmlContent,
    };
  };

  // Handle view email content - use already fetched data, no API call needed
  const handleViewEmail = () => {
    if (!selectedEmailDetail) return;

    const decodedContent = decodeAndReplaceContent(selectedEmailDetail);
    setEmailContent(decodedContent);
    setSelectedEmailDetail(null); // Close the details modal
  };

  // Close email content modal
  const handleCloseEmailContent = () => {
    setEmailContent(null);
  };

  // Close email details modal
  const handleCloseDetails = () => {
    setSelectedEmailDetail(null);
  };

  // Format date to MM/DD/YYYY, HH:MM AM/PM
  // Missing values are rendered as an em dash. The falsy check is needed alongside
  // the isNaN guard because new Date(null) coerces to epoch 0 and would otherwise
  // render a plausible-looking 01/01/1970 instead of signalling "no value".
  const formatDate = (dateString: string): string => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${month}/${day}/${year}, ${hours}:${minutes} ${ampm}`;
  };

  // Handle row click - fetch full log details
  const handleRowClick = async (log: EmailLog) => {
    setSelectedEmailDetail(null);

    try {
      const response = await emailLogsApi.getEmailContent(log.id);
      if (response?.data?.success) {
        setSelectedEmailDetail(response.data.data);
      }
    } catch (error) {
      handleError(error);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container py-2 sm:p-6" aria-busy="true">
        <Skeleton className="w-40 h-4 mb-4" />
        <Skeleton className="w-48 h-7 mb-2" />
        <Skeleton className="w-64 h-4 mb-6" />
        <div className="overflow-hidden bg-white border rounded-lg border-neutral-200">
          <table className="min-w-full">
            <tbody>
              <SkeletonTableRows rows={5} columns={4} />
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Header only — the rows are composed below so each one can stay clickable.
  const logColumns: DataTableColumn<EmailLog>[] = [
    { key: 'recipient', header: 'Recipient Name' },
    { key: 'scheduled', header: 'Scheduled' },
    { key: 'sentAt', header: 'Sent At' },
    { key: 'status', header: 'Status' },
  ];

  return (
    <div className="dashboard-container py-2 sm:p-6">
      <PageHeader
        breadcrumbs={[
          HOME_CRUMB,
          { label: 'Email', to: '/email' },
          { label: 'Email Logs', to: '/email?tab=logs' },
          { label: pitchInfo?.releaseTitle || 'Pitch' },
        ]}
        className="mb-6"
        title="Email Logs"
        description={
          <>
            {pitchInfo && (
              <span className="block">
                {pitchInfo.artistName} - {pitchInfo.releaseTitle}
              </span>
            )}
            {emailLogs.length > 0 && (
              <span className="block mt-1 text-xs text-neutral-500">
                {emailLogs.length} email{emailLogs.length !== 1 ? 's' : ''} sent
              </span>
            )}
          </>
        }
        actions={
          <Button
            variant="secondary"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />
      {/* <button
        onClick={handleCheckBounced}
        disabled={isRefreshing}
        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-warning-700 bg-warning-50 border border-warning-200 rounded-md hover:bg-warning-100 disabled:opacity-50 transition-colors"
      >
        <ExclamationTriangleIcon className="w-4 h-4" />
        Check Bounced
      </button> */}

      {/* Logs Table */}
      {emailLogs.length === 0 ? (
        <div className="py-12 text-center bg-white border rounded-lg border-neutral-200">
          <EnvelopeIcon className="w-12 h-12 mx-auto text-neutral-400" />
          <p className="mt-2 text-sm font-medium text-black">No email logs found</p>
          <p className="mt-1 text-sm text-neutral-500">Nothing has been sent for this pitch yet.</p>
        </div>
      ) : (
        <div aria-busy={isRefreshing}>
          {/*
            Refreshing swaps the rows for skeletons rather than dimming them
            behind a spinner. The old overlay left the previous results readable
            at 50% opacity while they were being replaced, which invites acting
            on a status that is mid-update; skeletons say plainly that the values
            are not current yet. Row count is held steady so nothing shifts.
          */}

          {/* Desktop Table View */}
          <div className="hidden md:block">
            <DataTable
              columns={logColumns}
              loading={isRefreshing}
              skeletonRows={Math.min(Math.max(emailLogs.length, 3), 8)}
            >
              {emailLogs.map((log) => (
                <tr
                  key={log.id}
                  onClick={() => handleRowClick(log)}
                  className={cn(dataTableRowClass, 'cursor-pointer')}
                >
                  <td className={dataTableTdClass}>
                    <div className="font-medium text-black">
                      {log.contact.firstName} {log.contact.lastName}
                    </div>
                    <div className="text-xs text-neutral-500">{log.contact.email}</div>
                  </td>
                  <td className={cn(dataTableTdClass, 'text-neutral-600')}>
                    {formatDate(log.scheduledFor)}
                  </td>
                  <td className={cn(dataTableTdClass, 'text-neutral-600')}>
                    {formatDate(log.sendAt)}
                  </td>
                  <td className={dataTableTdClass}>
                    <EmailStatusBadge status={log.status} />
                  </td>
                </tr>
              ))}
            </DataTable>
          </div>

          {/* Mobile Card View */}
          <div className="bg-white border rounded-lg md:hidden border-neutral-200 divide-y divide-neutral-100">
            {isRefreshing
              ? Array.from({ length: Math.min(Math.max(emailLogs.length, 3), 8) }).map((_, i) => (
                  <div key={i} className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0 space-y-2">
                        <Skeleton className="w-32 h-4" />
                        <Skeleton className="w-40 h-3" />
                        <Skeleton className="w-24 h-3" />
                      </div>
                      <Skeleton className="w-20 h-5 rounded-full shrink-0" />
                    </div>
                  </div>
                ))
              : emailLogs.map((log) => (
                  <div
                    key={log.id}
                    onClick={() => handleRowClick(log)}
                    className="p-4 transition-colors cursor-pointer hover:bg-primary-50"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-black truncate">
                          {log.contact.firstName} {log.contact.lastName}
                        </p>
                        <p className="text-xs truncate text-neutral-500">{log.contact.email}</p>
                        <p className="mt-1 text-xs text-neutral-500">{formatDate(log.sendAt)}</p>
                      </div>
                      <EmailStatusBadge status={log.status} />
                    </div>
                  </div>
                ))}
          </div>
        </div>
      )}

      {/* Email Details Modal */}
      <EmailDetailsModal
        email={selectedEmailDetail}
        isResending={isResending}
        onClose={handleCloseDetails}
        onViewEmail={handleViewEmail}
        onResend={handleResendEmail}
      />

      {/* Email Content Modal */}
      <EmailContentModal
        emailContent={emailContent}
        onClose={handleCloseEmailContent}
      />
    </div>
  );
};

export default EmailLogs;
